import { Box, CircularProgress } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'

/**
 * VERA-304 — pull down at the top of a list to refetch it.
 *
 * WHY THIS EXISTS, and why it changes the polling budget.
 *
 * The certificates list polls every N seconds so an arriving gift appears without
 * the recipient doing anything (the case that sets the cadence is two people
 * standing together: one sends, the other is watching their phone). A poll is a
 * guess about when the user wants fresh data, so the interval has to be short
 * enough to cover the impatient case — which means paying for a request every N
 * seconds per open app, forever, mostly when nothing has changed.
 *
 * An explicit gesture is strictly better information than a guess: it says "I want
 * it NOW". Once the user has that, the background interval only has to cover the
 * passive case, so it can relax (10s -> 20s here, halving background request
 * volume). This component is what makes that trade payable.
 *
 * POINTER EVENTS, not touch events. The gesture is a phone idiom, but Pointer
 * Events unify mouse/touch/pen, which means (a) it works with a trackpad drag on
 * desktop for free, and (b) it is verifiable in a desktop preview pane — a
 * touch-only implementation cannot be exercised there at all, and an unverifiable
 * component is one you find out about from a user.
 *
 * NATIVE PULL-TO-REFRESH IS SUPPRESSED while this is mounted
 * (`overscroll-behavior-y: contain` on the document element). That is not
 * incidental — without it, dragging down at scroll-top triggers the BROWSER's
 * pull-to-refresh (a full page reload) and the two gestures fight. The trade is
 * deliberate: a full reload re-boots the SPA and refetches everything; this
 * refetches one query and keeps the user's place. The cost is that the browser's
 * own reload gesture is no longer available on this screen.
 */

/** Past this many CSS px of pull, releasing triggers the refresh. */
const TRIGGER_DISTANCE = 64
/** Pull is damped so the sheet follows the finger at a decreasing rate. */
const RESISTANCE = 2.2
/** Hard cap so a long drag cannot push the content arbitrarily far down. */
const MAX_PULL = 96

export type PullToRefreshProps = {
  /** Called on release past the threshold. The indicator shows until it settles. */
  onRefresh: () => Promise<unknown> | unknown
  children: React.ReactNode
  /** Opt out (e.g. while a modal owns the gesture). */
  disabled?: boolean
}

export const PullToRefresh: React.FC<PullToRefreshProps> = ({
  onRefresh,
  children,
  disabled = false,
}) => {
  const [pull, setPull] = useState(0)
  const [refreshing, setRefreshing] = useState(false)

  // Refs, not state, for the gesture bookkeeping: these change on every pointermove
  // and must not each queue a render.
  const startY = useRef<number | null>(null)
  const active = useRef(false)

  // Suppress the browser's own pull-to-refresh for as long as this is mounted, and
  // restore whatever was there before on unmount — this is a global style, so
  // stomping it unconditionally would leak out to every other screen.
  useEffect(() => {
    if (disabled) return
    const el = document.documentElement
    const previous = el.style.overscrollBehaviorY
    el.style.overscrollBehaviorY = 'contain'
    return () => {
      el.style.overscrollBehaviorY = previous
    }
  }, [disabled])

  const reset = useCallback(() => {
    startY.current = null
    active.current = false
    setPull(0)
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled || refreshing) return
      // Only a gesture that begins AT THE TOP is a pull-to-refresh. Starting it
      // mid-list would hijack a normal scroll.
      if (window.scrollY > 0) return
      startY.current = e.clientY
      active.current = true
    },
    [disabled, refreshing],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!active.current || startY.current === null || refreshing) return
      const delta = e.clientY - startY.current
      if (delta <= 0) {
        // Upward — this is a scroll, not a pull. Yield the gesture entirely rather
        // than holding a partial pull that would snap back when they scroll away.
        reset()
        return
      }
      // If the page scrolled under us (momentum, anchor jump), abandon: a pull that
      // continues while the list scrolls reads as the content being stuck.
      if (window.scrollY > 0) {
        reset()
        return
      }
      setPull(Math.min(delta / RESISTANCE, MAX_PULL))
    },
    [refreshing, reset],
  )

  const onPointerUp = useCallback(async () => {
    if (!active.current || refreshing) return
    const shouldRefresh = pull >= TRIGGER_DISTANCE
    active.current = false
    startY.current = null

    if (!shouldRefresh) {
      setPull(0)
      return
    }

    // Hold the indicator at the trigger point while the request is in flight, so
    // the spinner does not jump position between "pulled" and "refreshing".
    setPull(TRIGGER_DISTANCE)
    setRefreshing(true)
    try {
      await onRefresh()
    } finally {
      setRefreshing(false)
      setPull(0)
    }
  }, [onRefresh, pull, refreshing])

  const armed = pull >= TRIGGER_DISTANCE
  const visible = pull > 0 || refreshing

  return (
    <Box
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={reset}
      // The gesture is vertical; let the browser keep horizontal panning and
      // pinch-zoom, which `touch-action: none` would also swallow.
      sx={{ touchAction: 'pan-x pinch-zoom', position: 'relative' }}
    >
      <Box
        aria-hidden={!visible}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: TRIGGER_DISTANCE,
          // Ride just above the content so it is revealed by the pull rather than
          // overlapping the first card.
          transform: `translateY(${pull - TRIGGER_DISTANCE}px)`,
          opacity: visible ? 1 : 0,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        <CircularProgress
          size={22}
          thickness={4}
          // Determinate while pulling gives honest feedback about how much further
          // is needed; indeterminate once it is actually loading.
          variant={refreshing ? 'indeterminate' : 'determinate'}
          value={refreshing ? undefined : Math.min((pull / TRIGGER_DISTANCE) * 100, 100)}
          sx={{ color: armed || refreshing ? 'text.primary' : 'text.disabled' }}
        />
      </Box>

      <Box
        sx={{
          transform: `translateY(${pull}px)`,
          // Animate only the SNAP BACK. Animating during the drag would make the
          // content lag the finger, which reads as jank rather than smoothness.
          transition: active.current ? 'none' : 'transform .2s ease-out',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export default PullToRefresh
